import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Dépannage menuiseries Montpellier' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Dépannage et réparation de menuiseries à Montpellier',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'City', name: 'Montpellier' },
  description: 'Réparation de baies coulissantes, volets roulants, vitrages, stores et fenêtres à Montpellier. Intervention 24-48h, 7j/7. Devis gratuit, garantie 10 ans.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR', description: 'Devis gratuit' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Intervenez-vous dans le centre historique de Montpellier (Écusson) ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, nos techniciens interviennent dans tout le centre historique, y compris les ruelles étroites de l\'Écusson. Nous nous adaptons aux contraintes des immeubles anciens et des copropriétés. Nous couvrons également Antigone, Port Marianne, Les Arceaux, Boutonnet et toute la métropole.',
      },
    },
    {
      '@type': 'Question',
      name: 'Quel est votre délai d\'intervention sur Montpellier ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Notre délai standard est de 24 à 48h après votre appel, 7 jours sur 7. Nous pouvons souvent intervenir encore plus rapidement dans Montpellier grâce à notre implantation locale. Pour une vitre cassée ou une baie bloquée en urgence, appelez le 09 80 80 22 42.',
      },
    },
    {
      '@type': 'Question',
      name: 'Réparez-vous les baies coulissantes des résidences récentes de Port Marianne ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Absolument. Nous intervenons sur tous les types de baies coulissantes aluminium, des plus anciennes de l\'Écusson aux plus récentes de Port Marianne. Nos techniciens maîtrisent tous les systèmes : Technal, Sepalumic, Schüco, K-Line et autres marques.',
      },
    },
  ],
};

export default function MontpellierPage() {
  return (
    <>
      <SEO
        title="Réparation baie coulissante Montpellier | DFPRO"
        description="Baie coulissante qui coince, volet roulant bloqué ou vitre cassée à Montpellier ? DFPRO intervient 24-48h, 7j/7. Devis gratuit, garantie 10 ans."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/depannage-menuiserie-montpellier/"
      />
      <ServicePageLayout
        heroImage="/images/ville-herault-01.jpg"
        title="Réparation de menuiseries et baie coulissante à Montpellier"
        subtitle="Baies coulissantes, volets roulants, vitrages : nos techniciens se déplacent dans tout Montpellier — Écusson, Antigone, Port Marianne. Devis gratuit, intervention 24-48h, 7j/7."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        ctaTitle="Un dépannage urgent à Montpellier ?"
        ctaText="Devis gratuit, intervention sous 24 à 48h, garantie 10 ans. Nos techniciens se déplacent dans tout Montpellier et sa métropole, 7j/7."
        ctaServiceLabel="Voir nos services"
      >
        <p className="text-lg text-secondary-500 leading-relaxed">
          Nous intervenons à Montpellier pour toute panne de menuiserie : baie coulissante qui coince ou déraille, volet roulant bloqué, vitre cassée, store en panne. De l'Écusson à Port Marianne, nos techniciens diagnostiquent et réparent sur place avec des pièces adaptées. Appelez-nous au <a href="tel:+33980802242" className="text-accent-500 font-semibold hover:underline">09 80 80 22 42</a>.
        </p>

        {/* Bloc 1 : Baie coulissante — Image gauche, Texte droite */}
        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/montpellier-baie-01.jpg"
              alt="Réparation de baie vitrée coulissante à Montpellier"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Baie coulissante qui coince ou déraille à Montpellier</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Une baie coulissante qui devient dure à manœuvrer, c'est le signe que les roulettes ou galets sont usés. À Montpellier, la chaleur estivale et l'usage intensif dans les copropriétés accélèrent cette usure. Si vous forcez, vous risquez d'endommager le rail ou de faire dérailler le vantail — une réparation bien plus coûteuse.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              Nous remplaçons les <Link to="/changer-roulettes-galets-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">roulettes et galets</Link> par des modèles renforcés, réparons les <Link to="/reparation-rail-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">rails déformés</Link> et effectuons le <Link to="/reglage-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réglage complet</Link> de vos vantaux. Toutes marques : Technal, Sepalumic, Schüco.
            </p>
            <Link to="/reparation-baie-coulissante-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Voir le dépannage baie coulissante <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        {/* Bloc 2 : Volet roulant — Texte gauche, Image droite */}
        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Volet roulant bloqué ou moteur en panne à Montpellier</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Un volet roulant qui ne remonte plus en plein été à Montpellier, c'est une urgence. Les causes sont variées : moteur qui a lâché, lame désaxée, sangle craquelée par les UV, ou condensateur hors service sur un modèle électrique. Dans les résidences des Arceaux ou les villas de Port Marianne, nous diagnostiquons la panne en quelques minutes.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              Nous réparons tous types de <Link to="/reparation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">volets roulants</Link> : électriques filaires et radio, manuels à sangle, solaires. Remplacement de <Link to="/reparation-moteur-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">moteur</Link>, <Link to="/changer-sangle-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">sangle</Link>, ou <Link to="/motorisation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">motorisation</Link> complète.
            </p>
            <Link to="/reparation-volet-roulant-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Voir le dépannage volet roulant <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/montpellier-volet-02.jpg"
              alt="Réparation de volet roulant à Montpellier"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        {/* Bloc 3 : Vitrage & Fenêtre — Image gauche, Texte droite */}
        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/montpellier-vitrage-03.webp"
              alt="Remplacement de vitrage et réparation de fenêtre à Montpellier"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Vitrage cassé, double vitrage embué, fenêtre qui ferme mal</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Vitre brisée dans un appartement de l'Écusson ou double vitrage qui s'opacifie dans une tour de Port Marianne : nous intervenons en urgence pour sécuriser votre logement. Un <Link to="/double-vitrage-embue-herault/" className="text-accent-500 font-semibold hover:underline">double vitrage embué</Link> signale un joint périphérique défaillant — il faut remplacer le vitrage, pas la fenêtre entière.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              Côté fenêtres, nous changeons <Link to="/changer-cremone-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">crémones</Link>, <Link to="/changer-joint-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">joints d'étanchéité</Link> et réglons les <Link to="/reglage-oscillo-battant-herault/" className="text-accent-500 font-semibold hover:underline">oscillo-battants</Link> qui ne ferment plus correctement. Service <Link to="/vitrier-herault/" className="text-accent-500 font-semibold hover:underline">vitrier</Link> disponible 7j/7.
            </p>
            <Link to="/vitrier-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Voir le service vitrier <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <h2>Intervention à Montpellier sous 24 à 48h</h2>
        <p>
          Notre équipe couvre l'intégralité de Montpellier : l'Écusson, Antigone, Port Marianne, Les Arceaux, Boutonnet, Les Aubes, Beaux-Arts, Hôpitaux-Facultés, le Millénaire et tous les secteurs environnants. Nous desservons également les communes limitrophes : Castelnau-le-Lez, Lattes, Juvignac, Saint-Jean-de-Védas et Clapiers. Appelez-nous au <a href="tel:+33980802242" className="text-accent-500 font-semibold hover:underline">09 80 80 22 42</a> pour un dépannage immédiat à Montpellier.
        </p>

        <div className="my-6 p-5 bg-accent-100/50 rounded-lg border border-accent-200/50">
          <p className="font-heading font-semibold text-primary-500 mb-2">Dépannage menuiseries à Montpellier et dans l'Hérault</p>
          <p className="text-sm text-secondary-500">
            Nous intervenons aussi dans les communes voisines : <Link to="/depannage-menuiserie-lattes/" className="text-accent-500 font-semibold hover:underline">Lattes</Link>, <Link to="/depannage-menuiserie-castelnau-le-lez/" className="text-accent-500 font-semibold hover:underline">Castelnau-le-Lez</Link> et <Link to="/depannage-menuiserie-mauguio/" className="text-accent-500 font-semibold hover:underline">Mauguio</Link>. Pour tout le département, visitez notre <Link to="/" className="text-accent-500 font-semibold hover:underline">page d'accueil DFPRO Hérault</Link>.
          </p>
        </div>
      </ServicePageLayout>
    </>
  );
}